import { Link } from 'react-router-dom';
import { Star, MapPin, ShieldCheck, MessageSquare, ArrowRight } from 'lucide-react';

const GRADIENT_PALETTES = [
  'from-violet-500 to-indigo-600',
  'from-sky-500 to-cyan-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-rose-600',
  'from-pink-500 to-purple-600',
  'from-amber-500 to-orange-600',
];

function getGradient(name = '') {
  const idx = name.charCodeAt(0) % GRADIENT_PALETTES.length;
  return GRADIENT_PALETTES[idx];
}

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={13}
          className={
            star <= Math.round(rating)
              ? 'fill-amber-400 text-amber-400'
              : 'fill-gray-200 text-gray-200'
          }
        />
      ))}
    </div>
  );
}

/**
 * BusinessLogo — displays logo image (contained, with white bg) or gradient initial
 */
function BusinessLogo({ avatarUrl, businessName, gradient, sizeClass = 'w-14 h-14', textSize = 'text-xl' }) {
  const initial = (businessName || 'B').charAt(0).toUpperCase();
  return (
    <div className={`${sizeClass} rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex-shrink-0`}>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={`${businessName || 'Business'} logo`}
          className="w-full h-full object-contain p-1.5"
          onError={(e) => {
            // fallback: show the gradient div
            e.target.style.display = 'none';
            if (e.target.nextElementSibling) {
              e.target.nextElementSibling.style.display = 'flex';
            }
          }}
        />
      ) : null}
      {/* Fallback gradient avatar — hidden when real logo is shown */}
      <div
        className={`w-full h-full bg-gradient-to-br ${gradient} items-center justify-center ${avatarUrl ? 'hidden' : 'flex'}`}
      >
        <span className={`text-white font-black select-none ${textSize}`}>{initial}</span>
      </div>
    </div>
  );
}

/* ─── GRID CARD ──────────────────────────────────────────────── */
export const BusinessGridCard = ({ business }) => {
  const gradient = getGradient(business?.business_name);
  const rating = business?.avg_rating || 0;
  const reviewCount = business?.total_reviews || 0;
  const location = business?.city && business?.country
    ? `${business.city}, ${business.country}`
    : business?.city || business?.country || null;

  return (
    <Link to={`/businesses/${business.id}`} className="group block h-full">
      <div className="h-full flex flex-col bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">

        {/* Top accent strip */}
        <div className={`h-2 w-full bg-gradient-to-r ${gradient} flex-shrink-0`} />

        {/* Logo + name row */}
        <div className="px-5 pt-4 flex items-start gap-3">
          <BusinessLogo
            avatarUrl={business?.avatar_url}
            businessName={business?.business_name}
            gradient={gradient}
            sizeClass="w-14 h-14"
            textSize="text-xl"
          />
          <div className="min-w-0 flex-1 pt-1">
            {business?.is_verified && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 border border-emerald-100 text-xs font-semibold px-2 py-0.5 rounded-full mb-1">
                <ShieldCheck size={10} />
                Verified
              </span>
            )}
            <h3 className="text-base font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-sky-600 transition-colors">
              {business?.business_name || 'Unnamed Business'}
            </h3>
          </div>
        </div>

        {/* Location */}
        <div className="px-5 pt-2 flex-1">
          {location ? (
            <div className="flex items-center gap-1.5 text-gray-500 text-sm">
              <MapPin size={13} className="flex-shrink-0 text-gray-400" />
              <span className="line-clamp-1">{location}</span>
            </div>
          ) : (
            <div className="h-5" /> /* spacer when no location */
          )}
        </div>

        {/* Divider */}
        <div className="mx-5 my-3 border-t border-gray-100" />

        {/* Rating + Reviews */}
        <div className="px-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StarRating rating={rating} />
            <span className="text-sm font-bold text-gray-800">{rating.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-400 text-xs">
            <MessageSquare size={12} />
            <span>{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}</span>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-5 pb-4">
          <div className="flex items-center justify-between text-xs font-semibold text-sky-600 group-hover:text-sky-700">
            <span>View Details</span>
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

      </div>
    </Link>
  );
};

/* ─── LIST CARD ──────────────────────────────────────────────── */
export const BusinessListCard = ({ business }) => {
  const gradient = getGradient(business?.business_name);
  const rating = business?.avg_rating || 0;
  const reviewCount = business?.total_reviews || 0;
  const location = business?.city && business?.country
    ? `${business.city}, ${business.country}`
    : business?.city || business?.country || null;

  return (
    <Link to={`/businesses/${business.id}`} className="group block">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
        {/* Left accent bar */}
        <div className="flex">
          <div className={`w-1.5 flex-shrink-0 bg-gradient-to-b ${gradient}`} />

          <div className="flex-1 p-4 flex items-center gap-4 min-w-0">
            {/* Logo */}
            <BusinessLogo
              avatarUrl={business?.avatar_url}
              businessName={business?.business_name}
              gradient={gradient}
              sizeClass="w-16 h-16"
              textSize="text-2xl"
            />

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h3 className="text-base font-bold text-gray-900 line-clamp-1 group-hover:text-sky-600 transition-colors">
                  {business?.business_name || 'Unnamed Business'}
                </h3>
                {business?.is_verified && (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 border border-emerald-100 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">
                    <ShieldCheck size={10} />
                    Verified
                  </span>
                )}
              </div>
              {location && (
                <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-1.5">
                  <MapPin size={12} className="flex-shrink-0 text-gray-400" />
                  <span className="line-clamp-1">{location}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <StarRating rating={rating} />
                  <span className="text-sm font-bold text-gray-800">{rating.toFixed(1)}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400 text-xs">
                  <MessageSquare size={11} />
                  <span>{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}</span>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 text-gray-300 group-hover:text-sky-500 transition-colors">
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ─── DEFAULT EXPORT (Grid Card — backward compatible) ────────── */
const BusinessCard = (props) => <BusinessGridCard {...props} />;
export default BusinessCard;
